import React, { ReactElement } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import useTranslate from '../useTranslate';
import { connect } from 'react-redux';
import { RootState } from '../../store';
import { VatIdVerificationStatus } from '../../store/userDetailsSlice';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Postit from '../main/postit';
import useFeatureAccess from '../../util/useFeatureAccess';
import { ManagedPartnerDto } from '../../store/partnerManagerSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';
import { useNavigate } from 'react-router-dom';

interface Props {
    priceSuggestionAvailable?: boolean;
    showAddBankData?: boolean;
    vatinVerification?: VatIdVerificationStatus;
    selectedPartner: ManagedPartnerDto;
    mansionsWithMissingContact: number[] | null;
}

const HIGHLIGHT_KEYS = {
    ADD_BANK_DATA: 'add-bank-data',
    FIX_VATIN: 'fix-vatin',
    SET_PRICES: 'set-prices',
    ADD_OPERATIONAL_CONTACTS: 'add-operational-contacts',
} as const;

type HighlightKey = (typeof HIGHLIGHT_KEYS)[keyof typeof HIGHLIGHT_KEYS];

const FULL_LENGTH = 12;

function HighlightedActions(props: Readonly<Props>) {
    const navigate = useNavigate();
    const strings = useTranslate('HighlightedActions');

    const { canWriteFeature } = useFeatureAccess(props.selectedPartner);
    const hasWriteLocation = canWriteFeature('RESIDENTIAL_LOCATION');
    const hasWriteSettlement = canWriteFeature('MASTER_SETTLEMENT_DETAILS');
    const hasWriteContractData = canWriteFeature('MASTER_CONTRACT_DATA');
    const hasWriteOperationalContacts = canWriteFeature(
        'GENERAL_OPERATIONAL_CONTACTS'
    );

    const actions: ReactElement[] = [];

    if (hasWriteSettlement && props.showAddBankData) {
        actions.push(
            <Row
                key={HIGHLIGHT_KEYS.ADD_BANK_DATA}
                data-testid={`highlight-${HIGHLIGHT_KEYS.ADD_BANK_DATA}`}
            >
                <HighlightItem
                    rowKey={HIGHLIGHT_KEYS.ADD_BANK_DATA}
                    onClick={handleGoToProfile}
                    icon={faExclamationTriangle}
                    description={strings.addBankDataText}
                    action={strings.addBankDataAction}
                />
            </Row>
        );
    }

    const failedVatinVerification = props.vatinVerification === 'INVALID';
    if (hasWriteContractData && failedVatinVerification) {
        actions.push(
            <Row
                key={HIGHLIGHT_KEYS.FIX_VATIN}
                data-testid={`highlight-${HIGHLIGHT_KEYS.FIX_VATIN}`}
            >
                <HighlightItem
                    rowKey={HIGHLIGHT_KEYS.FIX_VATIN}
                    onClick={handleGoToProfile}
                    icon={faExclamationTriangle}
                    description={strings.addressVatinText}
                    action={strings.addressVatinAction}
                />
            </Row>
        );
    }

    if (hasWriteLocation && props.priceSuggestionAvailable) {
        actions.push(
            <Row
                key={HIGHLIGHT_KEYS.SET_PRICES}
                data-testid={`highlight-${HIGHLIGHT_KEYS.SET_PRICES}`}
            >
                <HighlightItem
                    rowKey={HIGHLIGHT_KEYS.SET_PRICES}
                    onClick={handleSetPricesClick}
                    description={strings.priceSuggestionText}
                    action={strings.priceSuggestionAction}
                />
            </Row>
        );
    }

    if (
        hasWriteOperationalContacts &&
        props.mansionsWithMissingContact?.length
    ) {
        actions.push(
            <Row
                key={HIGHLIGHT_KEYS.ADD_OPERATIONAL_CONTACTS}
                data-testid={`highlight-${HIGHLIGHT_KEYS.ADD_OPERATIONAL_CONTACTS}`}
            >
                <HighlightItem
                    rowKey={HIGHLIGHT_KEYS.ADD_OPERATIONAL_CONTACTS}
                    onClick={handleAddOperationalContactsClick}
                    icon={faExclamationTriangle}
                    description={strings.addOperationalContactsText}
                    action={strings.addOperationalContactsAction}
                />
            </Row>
        );
    }

    return <>{actions}</>;

    function handleSetPricesClick() {
        navigate('/select-location-suggested-prices');
    }

    function handleGoToProfile() {
        navigate('/profile');
    }

    function handleAddOperationalContactsClick() {
        navigate('/operational-contacts');
    }
}

interface HighlightItemProps {
    key?: string;
    onClick: () => void;
    icon?: IconProp;
    description: string;
    action: string;
    rowKey: HighlightKey;
}

function HighlightItem(props: Readonly<HighlightItemProps>) {
    return (
        <Col xs={FULL_LENGTH}>
            <Postit>
                {props.icon && (
                    <FontAwesomeIcon
                        className={'mr-2'}
                        icon={props.icon}
                        size={'lg'}
                    />
                )}
                {props.description}
                <div className="mt-2">
                    <Button
                        bsStyle={'link'}
                        onClick={handleClick}
                        data-testid={`${props.rowKey}-button`}
                    >
                        {props.action}
                    </Button>
                </div>
            </Postit>
        </Col>
    );

    function handleClick() {
        props.onClick();
    }
}

export default connect(
    (store: RootState) => ({
        priceSuggestionAvailable:
            store.partnerManager.specificPartner?.price_suggestion_available,
        showAddBankData:
            store.userDetails.settlementBillingDetails &&
            !store.userDetails.settlementBillingDetails.bank_data_available,
        vatinVerification:
            store.userDetails.contractDetails?.vat_id_verification_status,
        mansionsWithMissingContact:
            store.operationalContacts.operationalContacts
                ?.mansions_with_missing_contact,
    }),
    {}
)(HighlightedActions);
